'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';

interface Service {
  name: string;
  href: string;
}

interface MobileMenuProps {
  currentPage: string;
  phoneNumber: string;
  companyName: string;
  services: Service[];
  isActive: (path: string) => boolean;
}

const MobileMenu: React.FC<MobileMenuProps> = ({ 
  currentPage, 
  phoneNumber, 
  companyName, 
  services,
  isActive 
}) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="lg:hidden py-3 md:py-4">
      <div className="flex items-center justify-between">
        {/* Mobile Menu Button - Left */}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="text-gray-700 hover:text-slate-900 transition-colors p-2"
          aria-label="Toggle mobile menu"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {mobileMenuOpen ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            )}
          </svg>
        </button>

        {/* Logo - Center */}
        <div className="flex-1 flex justify-center">
          <Link href="/" className="flex items-center hover:opacity-80 transition-opacity">
            <Image
              src="/images/optimized/logo-mobile.webp"
              alt={companyName}
              width={200}
              height={48}
              className="h-8 w-auto"
              priority
              sizes="200px"
              placeholder="blur"
              blurDataURL="data:image/webp;base64,UklGRnoAAABXRUJQVlA4WAoAAAAQAAAADwAABwAAQUxQSDIAAAARL0AmbZurmr57yyIiqE8oiG0bejIYEQTgqiDA9vqnsUSI6H+oAERp2HZ65qP/VIAWAFZQOCBCAAAA8AEAnQEqEAAIAAVAfCWkAALp8sF8rgRgAP7o9FDvMCkMde9PK7euH5M1m6VWoDXf2FkP3BqV0ZYbO6NA/VFIAAAA"
            />
          </Link>
        </div>

        {/* Call Now - Right */}
        <a href={`tel:${phoneNumber}`} className="text-slate-800 font-bold text-sm hover:text-slate-900 transition-colors">
          Call Now
        </a>
      </div>

      {/* Mobile Navigation Menu */}
      {mobileMenuOpen && (
        <div className="border-t border-gray-200 py-4 mt-3">
          <nav className="flex flex-col space-y-4">
            <Link 
              href="/" 
              className={`px-2 py-1 transition-colors ${
                isActive('/') 
                  ? 'text-slate-800 font-semibold' 
                  : 'text-gray-700 hover:text-slate-900 font-medium'
              }`}
              onClick={() => setMobileMenuOpen(false)}
            >
              Home
            </Link>
            
            {/* Mobile Services Section */}
            <div className="px-2">
              <Link 
                href="/services" 
                className={`py-1 block transition-colors ${
                  isActive('/services') 
                    ? 'text-slate-800 font-semibold' 
                    : 'text-gray-700 hover:text-slate-900 font-medium'
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                All Services
              </Link>
              <div className="ml-4 mt-2 space-y-2">
                {services.map((service, index) => (
                  <Link
                    key={index}
                    href={service.href}
                    className={`text-sm block py-1 transition-colors ${
                      isActive(service.href) 
                        ? 'text-slate-800 font-medium' 
                        : 'text-gray-600 hover:text-slate-900'
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {service.name}
                  </Link>
                ))}
              </div>
            </div>
            
            <Link 
              href="/about" 
              className={`px-2 py-1 transition-colors ${
                isActive('/about') 
                  ? 'text-slate-800 font-semibold' 
                  : 'text-gray-700 hover:text-slate-900 font-medium'
              }`}
              onClick={() => setMobileMenuOpen(false)}
            >
              About
            </Link>
            <Link 
              href="/blog" 
              className={`px-2 py-1 transition-colors ${
                isActive('/blog') 
                  ? 'text-slate-800 font-semibold' 
                  : 'text-gray-700 hover:text-slate-900 font-medium'
              }`}
              onClick={() => setMobileMenuOpen(false)}
            >
              Blog
            </Link>
            <Link 
              href="/contact" 
              className={`px-2 py-1 transition-colors ${
                isActive('/contact') 
                  ? 'text-slate-800 font-semibold' 
                  : 'text-gray-700 hover:text-slate-900 font-medium'
              }`}
              onClick={() => setMobileMenuOpen(false)}
            >
              Contact
            </Link>
            <div className="pt-4 border-t border-gray-200">
              <a href={`tel:${phoneNumber}`} className="block text-xl font-bold text-slate-800 hover:text-slate-900 transition-colors px-2 py-2">
                {phoneNumber}
              </a>
              <Link href="/contact" className="block bg-slate-800 text-white px-4 py-3 rounded-lg font-semibold hover:bg-slate-900 transition-all duration-200 shadow-md text-center mt-2" onClick={() => setMobileMenuOpen(false)}>
                Schedule Service
              </Link>
            </div>
          </nav>
        </div>
      )}
    </div>
  );
};

export default MobileMenu;

